import { Dispatcher } from '@thoughtspot/blink-context';
import { translate as t } from '@thoughtspot/i18n';
import { print } from 'graphql/language/printer';
import {
    AcSession,
    ActionTypes,
    BachSessionId,
    EmbedActionConfig,
    GetChartWithDataDocument,
    GetTableWithHeadlineDataDocument,
} from '/@services/generated/graphql-types';
import { EventType, JsonStringtoArrayRequestHeaders } from '/@utils/embed.util';
import { ConvMetadata } from '../../components/conv-assist-answer/conv-assist-shared-types';

interface CustomActionServiceParams {
    action: EmbedActionConfig;
    embedAnswerData: any;
    authHeader: any;
    showToastAlert: any;
    showBannerAlert: any;
    bachSession: BachSessionId;
    acSession: AcSession;
    isChartMode: boolean;
    dispatchEmbedEvent: Dispatcher;
    convMetadata?: ConvMetadata;
}

export const customActionService = (data: CustomActionServiceParams) => {
    const {
        action,
        embedAnswerData,
        authHeader,
        showToastAlert,
        showBannerAlert,
        bachSession,
        acSession,
        isChartMode,
        dispatchEmbedEvent,
        convMetadata = {} as ConvMetadata,
    } = data;
    const isCustomActionUrl = action.type === ActionTypes.Url;

    const customActionUrl = async () => {
        const queryParams: any = {};
        JsonStringtoArrayRequestHeaders(
            action.detail.additionalUrlHeaders,
        ).forEach((e: any) => {
            queryParams[e.key] = e.value;
        });

        const url = new URL(action.detail.link);
        url.search = new URLSearchParams(queryParams).toString();
        try {
            const response = await fetch(url.href, {
                method: 'POST',
                body: JSON.stringify(embedAnswerData),
                headers: {
                    ...authHeader,
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
            });
            if (response.ok) {
                showToastAlert(t('Successful'));
            } else {
                const errorMessage = t('URL_ACTION_RESPONSE_FAILURE', {
                    actionName: action.name,
                    errorCode: response.status,
                });
                showBannerAlert(errorMessage);
            }
        } catch {
            const clusterDomain = window.location.host;
            const errorMessage = t('URL_ACTION_REQUEST_FAILURE', {
                actionName: action.name,
                actionURL: action.detail.link,
                clusterDomain,
            });
            showBannerAlert(errorMessage);
        }
    };

    const customActionCallback = () => {
        const operation = isChartMode
            ? 'GetChartWithData'
            : 'GetTableWithHeadlineData';

        dispatchEmbedEvent(EventType.CustomAction, {
            id: action.detail.function,
            embedAnswerData,
            operation,
            session: {
                ...bachSession,
                acSession: {
                    sessionId: acSession.sessionId,
                    genNo: acSession.genNo,
                },
            },
            query: (isChartMode
                ? print(GetChartWithDataDocument)
                : print(GetTableWithHeadlineDataDocument)
            ).replace('visualizationsLoading @client', ''), // We make a REST API call from outside TS using the graphql string as a param instead of using an Apollo client. But @client is provided by Apollo Client only, so we are replacing this with an empty string as it will not affect the result.
            ...convMetadata,
        });
    };
    return isCustomActionUrl ? customActionUrl() : customActionCallback();
};
